import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { FiStar, FiSend, FiCheckCircle } from "react-icons/fi";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import SectionTitle from "../components/SectionTitle";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";
import OmSymbol from "../components/OmSymbol";
import api, { imgUrl } from "../utils/api";

function initials(name = "") {
  return name
    .replace(/^(mr\.|mrs\.|ms\.)/i, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const inputClass =
  "input-premium w-full px-4 py-2.5 rounded-lg border border-gold-300 bg-white/70";

const emptyForm = { name: "", location: "", service: "", rating: 5, text: "" };

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = () => {
    api
      .get("/testimonials")
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error("Please share your name and your review.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/testimonials", form);
      toast.success(
        data.message || "Thank you! Your review will appear here once approved.",
        { duration: 5000 }
      );
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Average rating across all approved reviews
  const avgRating =
    items.length > 0
      ? (items.reduce((s, t) => s + (t.rating || 5), 0) / items.length).toFixed(1)
      : "5.0";

  return (
    <div>
      <SEO
        title="Devotee Reviews & Testimonials"
        description="Read genuine reviews from devotees who experienced authentic Vedic poojas by Pandit Saurabh Shastri and astrology guidance by Gauri Shastri in Thane & Mumbai."
        keywords="pandit reviews thane, astrologer testimonials thane, pooja feedback, saurabh shastri reviews, gauri shastri reviews"
        path="/testimonials"
      />

      <PageHero
        eyebrow="Devotee Experiences"
        title="Reviews & Testimonials"
        subtitle="The blessings and words of our devotees are our greatest reward. Read what families across Thane & Mumbai share about their sacred experiences with us."
      />

      {/* Rating summary band */}
      <section className="bg-maroon-900 bg-mandala text-white">
        <div className="max-w-7xl mx-auto px-4 relative z-10 grid grid-cols-3 divide-x divide-gold-900/40">
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">{avgRating}</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Avg. Rating</span>
          </div>
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">{items.length}</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Reviews</span>
          </div>
          <div className="flex flex-col items-center py-5">
            <span className="text-2xl sm:text-3xl font-heading text-gold-300">500+</span>
            <span className="text-[11px] sm:text-xs text-gold-100/60 mt-0.5">Poojas Performed</span>
          </div>
        </div>
      </section>

      {/* Reviews grid */}
      <section className="py-14 bg-saffron-50/60">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="What Devotees Say"
            title="Blessings From Our Devotees"
            subtitle="Every review is a testament to the devotion and care we bring to each ceremony and consultation."
          />

          {loading ? (
            <Loader label="Loading reviews..." />
          ) : items.length === 0 ? (
            <EmptyState
              title="No reviews yet"
              subtitle="Be the first to share your experience! Use the form below to leave a review."
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((t, i) => (
                <motion.div
                  key={t._id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                  className="relative bg-white rounded-2xl p-7 shadow-premium border border-gold-100 hover-lift overflow-hidden flex flex-col"
                >
                  {/* big decorative quote mark */}
                  <span className="absolute -top-2 right-4 font-heading text-7xl text-gold-200 select-none pointer-events-none">
                    &rdquo;
                  </span>

                  <div className="flex gap-1 text-gold-500 mb-3 relative z-10">
                    {[...Array(5)].map((_, idx) => (
                      <FiStar
                        key={idx}
                        size={15}
                        fill={idx < (t.rating || 5) ? "currentColor" : "none"}
                        className={idx < (t.rating || 5) ? "" : "text-gold-200"}
                      />
                    ))}
                  </div>

                  <p className="text-gray-700 italic leading-relaxed mb-5 relative z-10 flex-grow">
                    “{t.text}”
                  </p>

                  <div className="flex items-center gap-3 relative z-10 border-t border-gold-100 pt-4">
                    {t.image ? (
                      <img
                        src={imgUrl(t.image)}
                        alt={t.name}
                        className="w-11 h-11 rounded-full object-cover border border-gold-200"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-maroon-gradient text-gold-200 flex items-center justify-center font-heading text-sm shadow-gold ring-1 ring-gold-300/30">
                        {initials(t.name)}
                      </div>
                    )}
                    <div>
                      <p className="font-heading text-maroon-800 leading-tight">{t.name}</p>
                      {t.location && <p className="text-sm text-saffron-600">{t.location}</p>}
                      {t.service && (
                        <p className="text-[11px] text-gray-400 mt-0.5">Service: {t.service}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Leave a Review form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-gradient-to-br from-gold-50 to-saffron-50 rounded-3xl p-8 sm:p-10 border border-gold-200 shadow-premium">
            <div className="text-center mb-8">
              <span className="inline-flex justify-center text-3xl text-gold-500 mb-2">
                <OmSymbol size={32} />
              </span>
              <h3 className="font-heading text-2xl sm:text-3xl text-maroon-800">
                Share Your Experience
              </h3>
              <p className="text-gray-600 mt-2 text-sm">
                Were you happy with a pooja or consultation? Your review helps other devotees and blesses us
                with your kind words. Reviews are published after a quick approval.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Sunita Patil"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Thane West"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Which service did you take? (optional)
                </label>
                <input
                  type="text"
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="e.g. Satyanarayan Mahapuja"
                />
              </div>

              {/* Star rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-1"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    >
                      <FiStar
                        size={28}
                        className={n <= form.rating ? "text-gold-500" : "text-gray-300"}
                        fill={n <= form.rating ? "currentColor" : "none"}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-500">{form.rating} / 5</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Review *</label>
                <textarea
                  name="text"
                  value={form.text}
                  onChange={handleChange}
                  rows={5}
                  className={inputClass}
                  placeholder="Tell us about your experience..."
                  maxLength={1000}
                />
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <FiCheckCircle className="text-green-500" /> Reviews are moderated before appearing on the site.
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-maroon w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold disabled:opacity-60"
              >
                <FiSend /> {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
